import React from 'react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import BlogCard from '@/components/blog/BlogCard';
import { Search, ArrowRight } from 'lucide-react';

const Blog = () => {
  // Mock data - replace with real data from your API/CMS
  const featuredPost = {
    id: '1',
    title: 'Comment construire une startup tech qui dure ?',
    excerpt: 'Découvrez les étapes clés pour bâtir une startup technologique pérenne, de la validation de l\'idée au développement du MVP.',
    image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=2070&auto=format&fit=crop',
    category: 'Startup',
    date: '15 Mars 2024',
    readTime: '8 min'
  };

  const posts = [
    {
      id: '2',
      title: 'Les technologies à surveiller en 2024',
      excerpt: 'Notre analyse des technologies émergentes qui vont façonner le paysage numérique cette année.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop',
      category: 'Tech',
      date: '10 Mars 2024',
      readTime: '6 min'
    },
    {
      id: '3',
      title: 'React vs Vue : Quel framework choisir en 2024 ?',
      excerpt: 'Analyse comparative approfondie pour vous aider à choisir le meilleur framework pour votre projet.',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=2070&auto=format&fit=crop',
      category: 'Développement',
      date: '8 Mars 2024',
      readTime: '10 min'
    },
    {
      id: '4',
      title: 'L\'IA dans le développement : opportunités et défis',
      excerpt: 'Comment l\'intelligence artificielle transforme le métier de développeur.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2070&auto=format&fit=crop',
      category: 'IA',
      date: '5 Mars 2024',
      readTime: '7 min'
    },
    {
      id: '5',
      title: 'Sécurité des applications : bonnes pratiques',
      excerpt: 'Guide complet pour sécuriser vos applications web et mobiles.',
      image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?q=80&w=2070&auto=format&fit=crop',
      category: 'Sécurité',
      date: '3 Mars 2024',
      readTime: '9 min'
    },
    {
      id: '6',
      title: 'Architecture microservices : pour qui et pourquoi ?',
      excerpt: 'Les avantages et inconvénients des microservices expliqués simplement.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2034&auto=format&fit=crop',
      category: 'Architecture',
      date: '1 Mars 2024',
      readTime: '8 min'
    }
  ];

  return (
    <>
      <SEO 
        title="Blog Orcsys - Actualités Tech et Développement"
        description="Découvrez nos articles sur le développement, la tech, et les startups. Conseils d'experts, analyses et bonnes pratiques pour votre projet digital."
        keywords="blog tech, développement web, startup, innovation, conseils tech, actualités technologiques, expertise digitale, transformation numérique"
        type="website"
        url="https://innov.orcsys.fr/blog"
        image={featuredPost.image}
        section="Blog"
      />
      
      <div className="min-h-screen bg-slate-50">
        {/* Hero Section with Featured Post */}
        <section className="relative bg-white border-b border-slate-100 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.2)_0%,rgba(255,255,255,0)_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.15)_0%,rgba(255,255,255,0)_60%)]" />
          
          <div className="container px-4 mx-auto py-16 lg:py-20 relative z-10">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <motion.h1 
                className="text-4xl md:text-5xl font-bold text-slate-900 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Le Blog <span className="text-orcsys-500">Orcsys</span>
              </motion.h1>
              <motion.p 
                className="text-lg text-slate-600 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Découvrez nos derniers articles, analyses et conseils sur le développement, 
                la tech et l'entrepreneuriat.
              </motion.p>
            </div>

            {/* Featured Post */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="max-w-5xl mx-auto"
            >
              <BlogCard post={featuredPost} />
            </motion.div>
          </div>
        </section>

        {/* Articles Grid with Creative Layout */}
        <section className="py-16 lg:py-20">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  className={`${
                    index % 3 === 1 ? 'lg:mt-12' : 
                    index % 3 === 2 ? 'lg:mt-24' : ''
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <BlogCard post={post} />
                </motion.div>
              ))}
            </div>

            {/* Load More Button */}
            <motion.div 
              className="text-center mt-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <button className="inline-flex items-center px-6 py-3 bg-orcsys-500 hover:bg-orcsys-600 text-white font-medium rounded-xl transition-colors group">
                Charger plus d'articles
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog; 